import { useState, useRef, useEffect } from "react";
import { Bug, X, Paperclip, Loader2, Check } from "lucide-react";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

/**
 * Bug report modal — listens for the global `open-bug-report` window event so
 * the trigger can live anywhere (Sidebar, command palette, anywhere). The
 * floating bottom-right launcher was removed because it overlapped page
 * content. Admins never see this — they triage from the Admin panel.
 *
 * Trigger from anywhere:
 *   window.dispatchEvent(new Event("open-bug-report"))
 */
export default function BugReportButton() {
  const { t } = useT();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-bug-report", onOpen);
    return () => window.removeEventListener("open-bug-report", onOpen);
  }, []);

  if (!user || user.is_admin) return null;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) { toast.error(t("bug_file_too_large")); return; }
    const okType = f.type.startsWith("image/") || f.type === "application/pdf";
    if (!okType) { toast.error(t("bug_only_image_or_pdf")); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setAttachment({
        b64: dataUrl.split(",")[1],
        name: f.name,
        mime: f.type,
        dataUrl,
      });
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (message.trim().length < 3) { toast.error(t("bug_message_short")); return; }
    setSubmitting(true);
    try {
      await api.post("/support/reports", {
        message,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        attachment_base64: attachment?.b64 || null,
        attachment_name: attachment?.name || null,
        attachment_mime: attachment?.mime || null,
      });
      toast.success(t("bug_thanks"));
      setOpen(false);
      setMessage("");
      setAttachment(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("bug_send_fail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Mobile-only floating launcher — desktop users open from the Sidebar.
          Positioned above the mobile bottom nav so it never overlaps content. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="md:hidden fixed right-4 bottom-20 z-40 inline-flex items-center justify-center w-12 h-12 rounded-full bg-zinc-900 hover:bg-zinc-800 text-white shadow-lg shadow-zinc-900/30 active:scale-95 transition"
        data-testid="bug-report-launcher-mobile"
        title={t("bug_open")}
        aria-label={t("bug_open")}
      >
        <Bug className="w-5 h-5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="bug-report-modal"
        >
          <div
            className="bg-white border-2 border-zinc-900 rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-orange-600" />
                <h2 className="text-lg font-black tracking-tight">{t("bug_title")}</h2>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-zinc-100 rounded" data-testid="bug-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-zinc-600">{t("bug_sub")}</p>

              <textarea
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("bug_placeholder")}
                className="w-full px-3 py-2.5 border border-zinc-200 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 resize-none"
                data-testid="bug-message-input"
                maxLength={4000}
              />

              {attachment ? (
                <div className="flex items-center gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-md">
                  {attachment.mime.startsWith("image/") ? (
                    <img src={attachment.dataUrl} alt="" className="w-12 h-12 object-cover rounded" />
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded text-red-700 font-bold text-xs">PDF</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{attachment.name}</div>
                    <div className="text-xs text-zinc-500">{Math.round(attachment.b64.length * 0.75 / 1024)} KB</div>
                  </div>
                  <button
                    onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ""; }}
                    className="p-1 hover:bg-zinc-200 rounded"
                    data-testid="bug-attachment-remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 border-2 border-dashed border-zinc-300 hover:border-zinc-500 hover:bg-zinc-50 rounded-md text-sm text-zinc-600 font-semibold"
                  data-testid="bug-attach-btn"
                >
                  <Paperclip className="w-4 h-4" /> {t("bug_attach")}
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={onFile}
                className="hidden"
                data-testid="bug-file-input"
              />

              <button
                onClick={submit}
                disabled={submitting || message.trim().length < 3}
                className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white font-bold py-3 rounded-md disabled:opacity-50"
                data-testid="bug-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {submitting ? t("bug_sending") : t("bug_send")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
